/* ==========================================================================
   WQ GLOBAL EFFECTS ENGINE · wq-custom-script.js
   --------------------------------------------------------------------------
   Scroll-reveal system for WQ components. Decorates elements at runtime
   (zero markup changes — update-safe), staggers siblings, and disables
   itself under prefers-reduced-motion. Extend here for future effects;
   never edit native Shrine scripts.
   ========================================================================== */
(function () {
  'use strict';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  /* Targets: wq component building blocks. Grouped selectors stagger
     within their container; single selectors reveal individually. */
  var GROUPS = [
    '.wqb-grid > article',
    '.wqx-grid > .wqx-card',
    '.wqs-steps > article',
    '.wql-explain > article',
    '.wqts-badges > .wqts-badge',
    '.wqn-grid > .wqn-cell'
  ];
  var SINGLES = [
    '.wqb-heading', '.wqs-heading', '.wqe .copy',
    '.wqp-shell', '.wqc-shell', '.wqc2-table-wrap',
    '.wql-search', '.wqi-body',
    '.wq-policy__hero .wq-policy__shell', '.wq-policy__body', '.wq-policy__contact',
    '.wqch-copy', '.wqch-media'
  ];

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });

  function decorate(el, delay) {
    if (el.classList.contains('wq-io')) return;
    el.classList.add('wq-io');
    if (delay) el.style.setProperty('--wq-io-delay', delay + 'ms');
    io.observe(el);
  }

  function init(root) {
    GROUPS.forEach(function (sel) {
      var els = root.querySelectorAll(sel);
      els.forEach(function (el, i) { decorate(el, Math.min(i * 90, 450)); });
    });
    SINGLES.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { decorate(el, 0); });
    });
  }

  /* ------------------------------------------------------------------
     Hero heading: split into words, cascade in (aria-safe).
     ------------------------------------------------------------------ */
  function heroWords(root) {
    root.querySelectorAll('.wqh-copy h1').forEach(function (h1) {
      if (h1.dataset.wqWords) return;
      h1.dataset.wqWords = '1';
      var text = h1.textContent.trim();
      if (!text) return;
      h1.setAttribute('aria-label', text);
      h1.textContent = '';
      text.split(/\s+/).forEach(function (w, i) {
        var s = document.createElement('span');
        s.className = 'wq-word';
        s.textContent = w;
        s.setAttribute('aria-hidden', 'true');
        s.style.transitionDelay = (140 + i * 75) + 'ms';
        h1.appendChild(s);
        h1.appendChild(document.createTextNode(' '));
      });
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { h1.classList.add('is-live'); });
      });
    });
  }

  /* ------------------------------------------------------------------
     3D micro-tilt on cards. Pointer-fine + hover devices only.
     Max ~5deg — felt, not seen.
     ------------------------------------------------------------------ */
  function tilt(root) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    root.querySelectorAll('.card-wrapper, .wqx-card').forEach(function (el) {
      if (el.dataset.wqTilt) return;
      el.dataset.wqTilt = '1';
      el.classList.add('wq-tilt');
      el.addEventListener('mousemove', function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--wq-rx', (-y * 4.5).toFixed(2) + 'deg');
        el.style.setProperty('--wq-ry', (x * 5.5).toFixed(2) + 'deg');
      });
      el.addEventListener('mouseleave', function () {
        el.style.setProperty('--wq-rx', '0deg');
        el.style.setProperty('--wq-ry', '0deg');
      });
    });
  }

  /* ------------------------------------------------------------------
     Section parallax: one passive scroll listener + rAF writes a
     [-1, 1] progress var per section; CSS does the moving.
     ------------------------------------------------------------------ */
  var parEls = [];
  var parTicking = false;
  function parUpdate() {
    parTicking = false;
    var vh = window.innerHeight;
    parEls.forEach(function (sec) {
      var r = sec.getBoundingClientRect();
      if (r.bottom < -120 || r.top > vh + 120) return;
      var p = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
      sec.style.setProperty('--wq-par', Math.max(-1, Math.min(1, p)).toFixed(3));
    });
  }
  function parOnScroll() {
    if (!parTicking) { parTicking = true; requestAnimationFrame(parUpdate); }
  }
  function parallax(root) {
    var found = root.querySelectorAll('.wqb, .wqe, .wqc');
    found.forEach(function (sec) {
      if (sec.dataset.wqPar) return;
      sec.dataset.wqPar = '1';
      sec.classList.add('wq-par');
      parEls.push(sec);
    });
    if (parEls.length && !parallax.bound) {
      parallax.bound = true;
      window.addEventListener('scroll', parOnScroll, { passive: true });
      window.addEventListener('resize', parOnScroll, { passive: true });
    }
    parUpdate();
  }

  /* ------------------------------------------------------------------
     Menu powder burst: a restrained puff of "powder" particles on nav
     hover — ivory/stone dust with 2-3 brand-orange grains. One puff per
     item per 1.2s; desktop fine pointers only.
     ------------------------------------------------------------------ */
  function powderBurst(root) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var COLORS = ['#DDD4C7', '#C9BFB2', '#EDE9E3', '#A69C92', '#F5A623', '#E8620E'];
    root.querySelectorAll('header .header__menu-item, header .list-menu__item').forEach(function (el) {
      if (el.dataset.wqBurst) return;
      el.dataset.wqBurst = '1';
      el.addEventListener('mouseenter', function () {
        var now = Date.now();
        if (el.dataset.wqBurstAt && now - el.dataset.wqBurstAt < 1200) return;
        el.dataset.wqBurstAt = now;
        var r = el.getBoundingClientRect();
        var wrap = document.createElement('span');
        wrap.className = 'wq-burst';
        wrap.style.transform = 'translate(' + (r.left + r.width / 2) + 'px,' + (r.top + 4) + 'px)';
        for (var k = 0; k < 12; k++) {
          var p = document.createElement('i');
          var size = 2 + Math.random() * 3.5;
          p.style.width = p.style.height = size + 'px';
          /* mostly dust tones; grains 4-5 get brand orange (~20%) */
          p.style.background = COLORS[k < 9 ? Math.floor(Math.random() * 4) : 4 + Math.floor(Math.random() * 2)];
          p.style.transform = 'translate(0,0) scale(1)';
          wrap.appendChild(p);
        }
        document.body.appendChild(wrap);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            Array.prototype.forEach.call(wrap.children, function (p) {
              var ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.6; /* upward fan */
              var dist = 18 + Math.random() * 30;
              var dx = Math.cos(ang) * dist;
              var dy = Math.sin(ang) * dist - 6;
              p.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(.25)';
              p.style.opacity = '0';
            });
          });
        });
        setTimeout(function () { wrap.remove(); }, 760);
      });
    });
  }

  function boot(root) {
    init(root);
    heroWords(root);
    tilt(root);
    parallax(root);
    powderBurst(root);
    /* Safety fuse: if IntersectionObserver never fires (edge cases on some
       mobile browsers / editors), force-reveal anything already on screen,
       and never leave content hidden longer than 1.6s after boot. */
    setTimeout(function () {
      document.querySelectorAll('.wq-io:not(.is-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 1.1 && r.bottom > -40) el.classList.add('is-in');
      });
    }, 400);
    setTimeout(function () {
      document.querySelectorAll('.wq-io:not(.is-in)').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 1.1 && r.bottom > -40) el.classList.add('is-in');
      });
    }, 1600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Re-init inside the theme editor when sections re-render */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();

/* ==========================================================================
   WQ ANIMATED SVG LOGO
   Drives sections/wq-animated-logo.liquid. GSAP is used when available; a
   requestAnimationFrame fallback keeps the section functional if CDN loading
   is blocked.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var GSAP_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/gsap.min.js';
  var gsapPromise = null;
  var HEADER_LOGO_DEFAULTS = {
    mode: 'fallback-after-animation',
    duration: 1.7,
    stagger: .04,
    holdDuration: 2,
    fadeDuration: .8,
    loadGsapCdn: true
  };

  function bool(value) {
    return value === true || value === 'true';
  }

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function loadGsap(shouldLoad) {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (gsapPromise) return gsapPromise;

    /* Prefer the locally hosted copy (assets/wq-gsap.min.js), announced by
       any section via data-gsap-src. CDN is a fallback only, and only when
       the merchant left "Load GSAP from CDN" enabled. */
    var localEl = document.querySelector('[data-gsap-src]');
    var localSrc = localEl ? localEl.getAttribute('data-gsap-src') : null;
    if (!localSrc && !shouldLoad) return Promise.resolve(null);

    function inject(src) {
      return new Promise(function (resolve) {
        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = function () { resolve(window.gsap || null); };
        script.onerror = function () { resolve(null); };
        document.head.appendChild(script);
      });
    }

    gsapPromise = (localSrc ? inject(localSrc) : Promise.resolve(null)).then(function (g) {
      if (g) return g;
      return shouldLoad ? inject(GSAP_SRC) : null;
    });

    return gsapPromise;
  }

  function geometryLength(el) {
    if (typeof el.getTotalLength === 'function') {
      try { return el.getTotalLength(); } catch (err) { return 0; }
    }
    return 0;
  }

  function discoverDrawables(section) {
    var svgRoot = section.querySelector('[data-wq-logo-svg]');
    if (!svgRoot) return [];

    var drawables = svgRoot.querySelectorAll('path, line, polyline, polygon, circle, ellipse, rect');
    return Array.prototype.map.call(drawables, function (el) {
      var length = geometryLength(el);
      if (!length) return null;

      el.setAttribute('data-wq-drawable', '');
      el.style.strokeDasharray = length;
      el.style.strokeDashoffset = length;

      return { el: el, length: length };
    }).filter(Boolean);
  }

  function setPercent(section, value) {
    var percent = section.querySelector('[data-wq-logo-percent]');
    if (!percent) return;
    percent.textContent = Math.round(value * 100);
    /* auto-hide the counter shortly after the draw completes */
    var wrap = percent.closest('.wqalogo__percent') || percent.parentElement;
    if (value >= 1 && wrap && !wrap.dataset.wqPctHide) {
      wrap.dataset.wqPctHide = '1';
      setTimeout(function () { wrap.classList.add('wq-pct-hidden'); }, 900);
    }
  }

  function setProgress(section, items, value) {
    var progress = clamp(value, 0, 1);
    items.forEach(function (item) {
      item.el.style.strokeDashoffset = item.length * (1 - progress);
    });
    setPercent(section, progress);
    section.classList.toggle('is-wq-logo-filled', bool(section.dataset.showFill) && progress >= 0.995);
    section.classList.toggle('is-wq-logo-done', progress >= 0.995);
  }

  function scheduleCompletion(section, loop) {
    if (loop || section.dataset.wqLogoCompletionScheduled === 'true') return;
    section.dataset.wqLogoCompletionScheduled = 'true';

    var action = section.dataset.completeAction || 'stay';
    if (action !== 'fade' && action !== 'collapse') return;

    var hold = Math.max(toNumber(section.dataset.holdDuration, 2), 0);
    var fade = Math.max(toNumber(section.dataset.fadeDuration, .8), .1);
    var collapse = Math.max(toNumber(section.dataset.collapseDuration, .8), .1);
    section.style.setProperty('--wqalogo-fade-duration', fade + 's');
    section.style.setProperty('--wqalogo-collapse-duration', collapse + 's');

    window.setTimeout(function () {
      if (action === 'collapse' && section.classList.contains('wqalogo')) {
        section.style.setProperty('--wqalogo-current-height', section.scrollHeight + 'px');
      }

      section.classList.add('is-wq-logo-hidden');

      if (action === 'collapse' && section.classList.contains('wqalogo')) {
        window.requestAnimationFrame(function () {
          section.classList.add('is-wq-logo-collapsed');
        });
      }

      if (section.classList.contains('wq-header-animated-logo')) {
        var headerLogoLink = section.closest('.header__heading-link');
        if (headerLogoLink && section.dataset.restoreStaticLogo === 'true') {
          headerLogoLink.classList.add('wq-header-logo--fallback');
        }
      }
    }, hold * 1000);
  }

  function animateFallback(section, items, target, duration, loop) {
    var start = null;

    function tick(timestamp) {
      if (!start) start = timestamp;
      var elapsed = (timestamp - start) / 1000;
      var raw = clamp(elapsed / duration, 0, 1);
      var eased = 1 - Math.pow(1 - raw, 3);
      setProgress(section, items, target * eased);

      if (raw < 1) {
        window.requestAnimationFrame(tick);
      } else if (loop) {
        window.setTimeout(function () {
          setProgress(section, items, 0);
          start = null;
          window.requestAnimationFrame(tick);
        }, 700);
      } else {
        scheduleCompletion(section, false);
      }
    }

    window.requestAnimationFrame(tick);
  }

  function animateWithGsap(section, items, target, duration, stagger, loop, gsap) {
    var proxy = { progress: 0 };
    gsap.killTweensOf(proxy);
    section.classList.remove('is-wq-logo-filled');
    section.classList.remove('is-wq-logo-done');

    gsap.to(proxy, {
      progress: target,
      duration: duration,
      ease: 'power3.out',
      repeat: loop ? -1 : 0,
      repeatDelay: .7,
      yoyo: loop,
      onUpdate: function () {
        var base = proxy.progress;
        items.forEach(function (item, index) {
          var offset = stagger * index / Math.max(duration, .1);
          var local = clamp((base - offset) / Math.max(target - offset, .001), 0, 1) * target;
          item.el.style.strokeDashoffset = item.length * (1 - local);
        });
        setPercent(section, base);
        section.classList.toggle('is-wq-logo-filled', bool(section.dataset.showFill) && base >= 0.995 && !loop);
        section.classList.toggle('is-wq-logo-done', base >= 0.995 && !loop);
        if (base >= 0.995) scheduleCompletion(section, loop);
      }
    });
  }

  function play(section, items) {
    var target = clamp(toNumber(section.dataset.progress, 1), 0, 1);
    var duration = Math.max(toNumber(section.dataset.duration, 2.4), .1);
    var stagger = Math.max(toNumber(section.dataset.stagger, .06), 0);
    var loop = bool(section.dataset.loop);

    if (REDUCED_MOTION) {
      setProgress(section, items, target);
      return;
    }

    loadGsap(bool(section.dataset.gsapCdn)).then(function (gsap) {
      if (gsap) {
        animateWithGsap(section, items, target, duration, stagger, loop, gsap);
      } else {
        animateFallback(section, items, target, duration, loop);
      }
    });
  }

  function bindScroll(section, items) {
    function update() {
      var rect = section.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var raw = (viewport - rect.top) / (viewport + rect.height);
      setProgress(section, items, clamp(raw, 0, 1) * clamp(toNumber(section.dataset.progress, 1), 0, 1));
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  function headerLogoSvg(id) {
    var gradient1 = 'wq-header-logo-linear-gradient-' + id;
    var gradient2 = 'wq-header-logo-linear-gradient-1-' + id;

    return '<svg xmlns="http://www.w3.org/2000/svg" id="wq-header-logo-svg-' + id + '" data-name="WQ logo" viewBox="0 0 170.08 170.08" role="img" aria-label="WQ Worldwide Quality logo"><defs><linearGradient id="' + gradient1 + '" x1="142.39" x2="151.44" y1="108.21" y2="123.92" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#ea5514"/><stop offset="1" stop-color="#ffe200"/></linearGradient><linearGradient id="' + gradient2 + '" x1="27.76" x2="148.59" y1="83.05" y2="83.05" gradientUnits="userSpaceOnUse"><stop offset=".27" stop-color="#ea5514"/><stop offset="1" stop-color="#ffe200"/></linearGradient><style>.st1{fill:#ea5514}</style></defs><path d="m144.37 125.04-10.88-16.78c-2.29-3.28-1.5-7.8 1.78-10.09s7.8-1.5 10.09 1.78l10.88 16.78-2.51 8.31z" style="fill:url(#' + gradient1 + ')"/><text style="font-family:Montserrat-Bold,Montserrat;font-size:7.42px;font-weight:700;fill:#ea5514" transform="translate(42.28 125.09)"><tspan x="0" y="0">WORLDWIDE QUALITY</tspan></text><path d="M145.25 61.74c-11.75-11.75-30.86-11.75-42.61 0l-.09.09c-4.07 4.2-6.43 8.85-7.7 12.01-1.88 4.67-4.01 9.6-6.66 15.39l-16.14-32.3a7.25 7.25 0 0 0-6.48-4.01h-.09a7.23 7.23 0 0 0-6.47 4.17L44.12 88.81l-13.94-30c.2.8.33 1.63.33 2.49 0 5.73-4.58 10.36-10.28 10.48l3.06 6.59a8.2 8.2 0 0 1 3.46-.79c4.52 0 8.19 3.67 8.19 8.19 0 3.3-1.96 6.12-4.77 7.42l7.34 15.8a7.24 7.24 0 0 0 6.56 4.19h.01c2.81 0 5.36-1.62 6.56-4.17l15.13-32.23 16.18 32.39a7.21 7.21 0 0 0 6.59 4.01c2.78-.04 5.3-1.67 6.47-4.2l5.31-11.45c3.24-6.99 5.77-12.8 7.98-18.29.78-1.95 2.22-4.78 4.63-7.29 6.1-6.05 15.99-6.04 22.07.04 2.95 2.95 4.58 6.88 4.58 11.06s-1.63 8.1-4.58 11.06a15.67 15.67 0 0 1-15.44 3.96c-3.85-1.12-7.86 1.1-8.98 4.94s1.1 7.86 4.94 8.98a30.1 30.1 0 0 0 29.72-7.63c5.69-5.69 8.82-13.26 8.82-21.3s-3.13-15.61-8.82-21.3Z" class="st1"/><path d="M123.95 52.92c-8.05 0-15.61 3.13-21.3 8.82l-.09.09c-4.06 4.2-6.43 8.85-7.7 12.01-1.87 4.67-4.01 9.6-6.66 15.39l-16.14-32.3a7.27 7.27 0 0 0-6.57-4.01 7.23 7.23 0 0 0-6.47 4.17L44.13 88.81l-13.94-30c.2.8.33 1.63.33 2.49 0 5.73-4.58 10.36-10.28 10.48l3.06 6.59a8.2 8.2 0 0 1 3.46-.79c4.52 0 8.19 3.67 8.19 8.19 0 3.3-1.96 6.12-4.77 7.42l7.34 15.8a7.24 7.24 0 0 0 6.56 4.19h.02c2.81 0 5.36-1.62 6.56-4.17l15.13-32.23 16.18 32.39a7.25 7.25 0 0 0 6.48 4.01h.11c2.78-.04 5.3-1.67 6.47-4.2l5.31-11.45c3.24-6.99 5.77-12.8 7.98-18.29.78-1.95 2.22-4.78 4.63-7.29 2.95-2.93 6.86-4.54 11.01-4.54s8.1 1.63 11.06 4.58l9.3-.49.94-9.75c-5.69-5.69-13.26-8.82-21.3-8.82Z" style="fill:url(#' + gradient2 + ')"/><circle cx="16.04" cy="76.95" r="3.11" class="st1"/><circle cx="6.24" cy="59.27" r="3.38" class="st1"/><circle cx="5.68" cy="71.2" r="4.66" class="st1"/><circle cx="26.47" cy="85.77" r="4.66" class="st1"/><circle cx="20.01" cy="61.02" r="6.66" class="st1"/><text style="fill:#716e6d;font-family:Montserrat-Regular,Montserrat;font-size:14.63px" transform="translate(156.25 54.36)"><tspan x="0" y="0">®</tspan></text></svg>';
  }

  function initHeaderLogo(root) {
    var links = document.querySelectorAll('.header__heading-link.media');
    var config = getHeaderLogoConfig();

    if (config.mode === 'off' || config.mode === 'static-only') {
      links.forEach(resetHeaderLogo);
      return;
    }

    links.forEach(function (link, index) {
      if (link.dataset.wqHeaderLogoReady === 'true' && link.dataset.wqHeaderLogoMode === config.mode) return;
      resetHeaderLogo(link);

      var nativeLogo = link.querySelector('.header__heading-logo--main');
      if (!nativeLogo) return;

      var id = Date.now().toString(36) + '-' + index;
      var wrapper = document.createElement('span');
      var completeAction = config.mode === 'animation-only' ? 'stay' : 'fade';
      wrapper.className = 'wq-header-animated-logo';
      wrapper.setAttribute('data-wq-animated-logo', '');
      wrapper.setAttribute('data-trigger', 'load');
      wrapper.setAttribute('data-progress', '1');
      wrapper.setAttribute('data-duration', config.duration);
      wrapper.setAttribute('data-stagger', config.stagger);
      wrapper.setAttribute('data-loop', 'false');
      wrapper.setAttribute('data-show-fill', 'true');
      wrapper.setAttribute('data-show-percent', 'false');
      wrapper.setAttribute('data-complete-action', completeAction);
      wrapper.setAttribute('data-hold-duration', config.holdDuration);
      wrapper.setAttribute('data-fade-duration', config.fadeDuration);
      wrapper.setAttribute('data-collapse-duration', config.fadeDuration);
      wrapper.setAttribute('data-gsap-cdn', config.loadGsapCdn);
      wrapper.setAttribute('data-restore-static-logo', config.mode === 'fallback-after-animation');
      wrapper.innerHTML = '<span data-wq-logo-svg>' + headerLogoSvg(id) + '</span>';

      link.appendChild(wrapper);
      link.classList.add('wq-header-logo--animated');
      if (config.mode === 'animation-only') {
        link.classList.add('wq-header-logo--animation-only');
      }
      if (link.tagName.toLowerCase() !== 'a') {
        link.setAttribute('role', 'link');
        link.setAttribute('tabindex', '0');
        link.setAttribute('aria-label', link.getAttribute('aria-label') || 'Go to homepage');
        link.addEventListener('click', function () {
          window.location.href = '/';
        });
        link.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            window.location.href = '/';
          }
        });
      }
      link.dataset.wqHeaderLogoReady = 'true';
      link.dataset.wqHeaderLogoMode = config.mode;
      initSection(wrapper);
    });
  }

  function resetHeaderLogo(link) {
    var injected = link.querySelector('.wq-header-animated-logo');
    if (injected) injected.remove();
    link.classList.remove('wq-header-logo--animated', 'wq-header-logo--fallback', 'wq-header-logo--animation-only');
    delete link.dataset.wqHeaderLogoReady;
    delete link.dataset.wqHeaderLogoMode;
  }

  function getHeaderLogoConfig() {
    var configEl = document.querySelector('[data-wq-header-logo-config]');
    if (!configEl) return HEADER_LOGO_DEFAULTS;

    return {
      mode: configEl.dataset.mode || HEADER_LOGO_DEFAULTS.mode,
      duration: Math.max(toNumber(configEl.dataset.duration, HEADER_LOGO_DEFAULTS.duration), .1),
      stagger: Math.max(toNumber(configEl.dataset.stagger, HEADER_LOGO_DEFAULTS.stagger), 0),
      holdDuration: Math.max(toNumber(configEl.dataset.holdDuration, HEADER_LOGO_DEFAULTS.holdDuration), 0),
      fadeDuration: Math.max(toNumber(configEl.dataset.fadeDuration, HEADER_LOGO_DEFAULTS.fadeDuration), .1),
      loadGsapCdn: bool(configEl.dataset.loadGsapCdn)
    };
  }

  function initSection(section) {
    if (!section || section.dataset.wqAnimatedLogoReady === 'true') return;
    section.dataset.wqAnimatedLogoReady = 'true';

    var items = discoverDrawables(section);
    if (!items.length) return;

    setProgress(section, items, 0);

    var trigger = section.dataset.trigger || 'viewport';
    if (trigger === 'static') {
      setProgress(section, items, clamp(toNumber(section.dataset.progress, 1), 0, 1));
      return;
    }

    if (trigger === 'scroll') {
      bindScroll(section, items);
      return;
    }

    if (trigger === 'load' || !('IntersectionObserver' in window)) {
      play(section, items);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          play(section, items);
          io.unobserve(section);
        }
      });
    }, { threshold: .2 });

    io.observe(section);
  }

  function init(root) {
    initHeaderLogo(root);
    root.querySelectorAll('[data-wq-animated-logo]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();

/* ==========================================================================
   WQ CANVAS PARTICLE LOGO — raster-based logo assembly (no SVG needed)
   Reads [data-wq-logo-particles]: samples the PNG pixels, animates orange
   dots gathering into the logo, then crossfades to the original image so
   the final frame is pixel-perfect at any DPR. Honors reduced motion.
   ========================================================================== */
(function () {
  'use strict';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function build(el) {
    if (el.dataset.wqParticlesInit) return;
    el.dataset.wqParticlesInit = '1';
    if (REDUCED) return; /* static fallback image stays visible */

    var canvas = el.querySelector('canvas');
    var imgEl = el.querySelector('img');
    if (!canvas || !imgEl) return;

    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = el.dataset.image || imgEl.currentSrc || imgEl.src;

    img.onload = function () {
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        start();
      }, { threshold: 0.3 });
      io.observe(el);
    };

    function start() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var cssW = el.clientWidth || 520;
      var cssH = Math.round(cssW * (img.naturalHeight / img.naturalWidth));
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.height = cssH + 'px';
      var ctx = canvas.getContext('2d');

      /* sample pixels */
      var off = document.createElement('canvas');
      off.width = canvas.width; off.height = canvas.height;
      var octx = off.getContext('2d');
      octx.drawImage(img, 0, 0, off.width, off.height);
      var data;
      try { data = octx.getImageData(0, 0, off.width, off.height).data; }
      catch (e) { el.classList.add('is-settled'); return; }

      var density = parseInt(el.dataset.density, 10) || 3;
      var cols = 46 + density * 26;
      var step = Math.max(2, Math.round(off.width / cols));
      var parts = [];
      for (var y = 0; y < off.height; y += step) {
        for (var x = 0; x < off.width; x += step) {
          var i = (y * off.width + x) * 4;
          if (data[i + 3] > 120) {
            parts.push({
              tx: x, ty: y,
              x: Math.random() * off.width,
              y: Math.random() * off.height,
              c: 'rgb(' + data[i] + ',' + data[i + 1] + ',' + data[i + 2] + ')',
              d: Math.random() * 0.35
            });
          }
          if (parts.length > 6000) break;
        }
      }
      if (!parts.length) { el.classList.add('is-settled'); return; }

      var dur = (parseFloat(el.dataset.duration) || 2.4) * 1000;
      var r = step * 0.46;
      var t0 = null;
      el.classList.add('is-live');

      function ease(t) { return 1 - Math.pow(1 - t, 3); }
      function frame(ts) {
        if (!t0) t0 = ts;
        var raw = (ts - t0) / dur;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        var done = true;
        for (var k = 0; k < parts.length; k++) {
          var p = parts[k];
          var t = Math.min(1, Math.max(0, (raw - p.d) / 0.65));
          if (t < 1) done = false;
          var e = ease(t);
          var px = p.x + (p.tx - p.x) * e;
          var py = p.y + (p.ty - p.y) * e;
          ctx.fillStyle = p.c;
          ctx.globalAlpha = 0.25 + 0.75 * e;
          ctx.beginPath();
          ctx.arc(px, py, r, 0, 6.2832);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        if (!done) { requestAnimationFrame(frame); }
        else { setTimeout(function () { el.classList.add('is-settled'); }, 250); }
      }
      requestAnimationFrame(frame);
    }
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-wq-logo-particles]').forEach(build);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else { scan(document); }
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
})();

/* ============================================================
   WQ Clarity Engine menu highlight
   Finds the header nav link to the Advanced Routine Advisor,
   tags it .wq-ce-link, adds a tiny gradient AI star and a
   "Powered by AI" tooltip pill floating above on hover ([19]).
   ============================================================ */
(function () {
  function mark(root) {
    /* Top-level bar only. The tip is a pill that floats above the link, which
       works in the main bar and collides with the text inside a dropdown — when
       Find Your Routine was re-pointed at this page it inherited the decoration
       and wrapped onto three lines. The Ask Clarity call to action is excluded
       too: it is already a pill and carries its own star. */
    (root || document).querySelectorAll('header a.header__menu-item--main[href*="/pages/clarity-engine-ai"]:not([href$="#ask"])').forEach(function (a) {
      if (a.classList.contains('wq-ce-link')) return;
      a.classList.add('wq-ce-link');
      var txt = a.querySelector('.header__menu-item-text') || a.querySelector('span') || a;
      var label = document.createElement('span');
      label.className = 'wq-ce-label';
      while (txt.firstChild) { label.appendChild(txt.firstChild); }
      txt.appendChild(label);
      var spark = document.createElement('span');
      spark.className = 'wq-ce-spark';
      spark.textContent = '✦';
      spark.setAttribute('aria-hidden', 'true');
      label.appendChild(spark);
      var tip = document.createElement('span');
      tip.className = 'wq-ce-tip';
      var isSpanish = (document.documentElement.lang || '').toLowerCase().indexOf('es') === 0;
      tip.textContent = isSpanish ? 'Impulsado por IA' : 'Powered by AI';
      tip.setAttribute('aria-hidden', 'true');
      txt.appendChild(tip);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mark(document); });
  } else { mark(document); }
  document.addEventListener('shopify:section:load', function (e) { mark(e.target); });
})();

/* ============================================================
   WQ language switcher
   Discovers available locales from the hreflang alternate links
   Shopify renders on every localized page, then mounts a compact
   EN/ES pill at the end of the header inline menu (and in the
   mobile drawer). Plain links to the localized URL of the SAME
   page — click switches instantly, Shopify keeps you in-locale
   from there. Renders nothing when only one locale exists.
   ============================================================ */
(function () {
  function build() {
    if (document.querySelector('.wq-lang')) return;
    var alts = {};
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(function (l) {
      var code = (l.getAttribute('hreflang') || '').toLowerCase();
      if (!code || code === 'x-default') return;
      var short = code.split('-')[0];
      if (!alts[short]) alts[short] = l.getAttribute('href');
    });
    var locales = Object.keys(alts);
    if (locales.length < 2) return;
    var current = (document.documentElement.lang || 'en').split('-')[0].toLowerCase();
    function makePill() {
      var wrap = document.createElement('div');
      wrap.className = 'wq-lang';
      wrap.setAttribute('aria-label', current === 'es' ? 'Idioma' : 'Language');
      locales.forEach(function (code) {
        var a = document.createElement('a');
        a.href = alts[code];
        a.textContent = code.toUpperCase();
        a.setAttribute('hreflang', code);
        if (code === current) { a.className = 'is-active'; a.setAttribute('aria-current', 'true'); }
        wrap.appendChild(a);
      });
      return wrap;
    }
    var inline = document.querySelector('header .header__inline-menu > ul');
    if (inline) {
      var li = document.createElement('li');
      li.appendChild(makePill());
      inline.appendChild(li);
    }
    var drawer = document.querySelector('#menu-drawer .menu-drawer__navigation, .menu-drawer__inner-container');
    if (drawer) drawer.appendChild(makePill());
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else { build(); }
  document.addEventListener('shopify:section:load', build);
})();

/* ============================================================
   WQ locale-aware internal links
   Merchant URL settings are stored as root-relative paths. Add
   Shopify's current locale root so Spanish visitors do not fall
   back to English when following a WQ-owned link.
   ============================================================ */
(function () {
  function localizeLinks(root) {
    var localeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!localeRoot || localeRoot === '/') return;
    localeRoot = localeRoot.replace(/\/$/, '');
    (root || document).querySelectorAll('a[href^="/"]').forEach(function (link) {
      var href = link.getAttribute('href');
      if (!href || href.indexOf('//') === 0 || href === localeRoot || href.indexOf(localeRoot + '/') === 0) return;
      if (/^\/(?:admin|apps|challenge|localization)(?:\/|$)/.test(href)) return;
      link.setAttribute('href', localeRoot + href);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { localizeLinks(document); });
  } else { localizeLinks(document); }
  document.addEventListener('shopify:section:load', function (event) { localizeLinks(event.target); });
})();

/* ============================================================
   WQ Ingredient knowledge index
   Preserves crawlable Shopify pagination while adding A–Z,
   research-interest filters, search, and optional progressive
   loading. Filtering hydrates all paginated cards first so the
   result set always covers the complete Ingredient catalog.
   ============================================================ */
(function () {
  function init(root) {
    if (!root || root.dataset.wqxReady === 'true') return;
    root.dataset.wqxReady = 'true';

    var grid = root.querySelector('[data-wqx-grid]');
    if (!grid || !root.querySelector('[data-wqx-card]')) return;

    var search = root.querySelector('[data-wqx-search]');
    var count = root.querySelector('[data-wqx-visible-count]');
    var empty = root.querySelector('[data-wqx-empty]');
    var loadButton = root.querySelector('[data-wqx-load]');
    var pagination = root.querySelector('[data-wqx-pagination]');
    var resultLabel = root.dataset.resultsLabel || 'ingredient guides';
    var loadingLabel = root.dataset.loadingLabel || 'Loading…';
    var loadMode = root.dataset.loadMode || 'load_more';
    var state = { q: '', letter: 'all', goal: 'all' };
    var loading = false;
    var allLoaded = false;
    var debounceTimer;

    function cards() {
      return Array.prototype.slice.call(grid.querySelectorAll('[data-wqx-card]'));
    }

    function sortCards() {
      cards()
        .sort(function (a, b) {
          return (a.dataset.name || '').localeCompare(b.dataset.name || '', 'en', { sensitivity: 'base' });
        })
        .forEach(function (card) { grid.appendChild(card); });
    }

    function nextUrlFrom(scope) {
      var button = scope.querySelector('[data-wqx-load]');
      var link = scope.querySelector('[data-wqx-next-link]');
      return (button && button.dataset.nextUrl) || (link && link.getAttribute('href')) || '';
    }

    function setLoading(active) {
      loading = active;
      root.setAttribute('aria-busy', active ? 'true' : 'false');
      if (loadButton) {
        if (!loadButton.dataset.defaultLabel) loadButton.dataset.defaultLabel = loadButton.textContent.trim();
        loadButton.disabled = active;
        loadButton.textContent = active ? loadingLabel : loadButton.dataset.defaultLabel;
      }
    }

    async function fetchPage(url) {
      var response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Ingredient page request failed: ' + response.status);
      var html = await response.text();
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var fetchedRoot = parsed.getElementById(root.id);
      if (!fetchedRoot) throw new Error('Ingredient index was not present in the response');
      var known = new Set(cards().map(function (card) { return card.getAttribute('href'); }));
      fetchedRoot.querySelectorAll('[data-wqx-card]').forEach(function (card) {
        var href = card.getAttribute('href');
        if (!known.has(href)) {
          grid.appendChild(card.cloneNode(true));
          known.add(href);
        }
      });
      sortCards();
      return nextUrlFrom(fetchedRoot);
    }

    async function loadNext() {
      if (loading || allLoaded) return;
      var next = loadButton && loadButton.dataset.nextUrl;
      if (!next) {
        allLoaded = true;
        if (loadButton) loadButton.hidden = true;
        return;
      }
      setLoading(true);
      try {
        var following = await fetchPage(next);
        loadButton.dataset.nextUrl = following;
        if (!following) {
          allLoaded = true;
          loadButton.hidden = true;
        }
        applyFilters(false);
      } catch (error) {
        console.error('[WQ Ingredient Index]', error);
      } finally {
        setLoading(false);
      }
    }

    async function loadEntireCatalog() {
      if (allLoaded || loading) return;
      setLoading(true);
      try {
        var baseUrl = window.location.pathname;
        var next = await fetchPage(baseUrl);
        var guard = 0;
        while (next && guard < 20) {
          next = await fetchPage(next);
          guard += 1;
        }
        allLoaded = true;
        if (loadButton) loadButton.hidden = true;
        if (pagination) pagination.classList.add('is-expanded');
        updateAvailableLetters();
      } catch (error) {
        console.error('[WQ Ingredient Index]', error);
      } finally {
        setLoading(false);
      }
    }

    function updateAvailableLetters() {
      if (!allLoaded) return;
      var available = new Set(cards().map(function (card) { return card.dataset.letter; }));
      root.querySelectorAll('[data-wqx-letter]').forEach(function (button) {
        button.disabled = button.dataset.wqxLetter !== 'all' && !available.has(button.dataset.wqxLetter);
      });
    }

    function updateUrl() {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      Array.from(url.searchParams.keys()).forEach(function (key) {
        if (key === 'page' || key.indexOf('page_') === 0) url.searchParams.delete(key);
      });
      ['q', 'letter', 'goal'].forEach(function (key) {
        var value = state[key];
        var parameter = key === 'q' ? 'ingredient_q' : key;
        if (value && value !== 'all') url.searchParams.set(parameter, value);
        else url.searchParams.delete(parameter);
      });
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    function applyFilters(syncUrl) {
      var visible = 0;
      var normalizedQuery = state.q.replace(/[^a-z0-9]/g, '');
      cards().forEach(function (card) {
        var searchable = (card.dataset.search || card.dataset.name || '').toLowerCase();
        var matchesQuery =
          !state.q ||
          searchable.indexOf(state.q) !== -1 ||
          searchable.replace(/[^a-z0-9]/g, '').indexOf(normalizedQuery) !== -1;
        var matchesLetter = state.letter === 'all' || card.dataset.letter === state.letter;
        var cardGoals = (card.dataset.goals || '').split('|');
        var matchesGoal = state.goal === 'all' || cardGoals.indexOf(state.goal) !== -1;
        var show = matchesQuery && matchesLetter && matchesGoal;
        card.hidden = !show;
        if (show) visible += 1;
      });
      if (count) count.textContent = visible;
      if (empty) empty.hidden = visible !== 0;
      root.setAttribute('data-visible-results', String(visible));
      root.setAttribute('aria-label', visible + ' ' + resultLabel);
      if (syncUrl !== false) updateUrl();
    }

    async function filterAfterHydration() {
      await loadEntireCatalog();
      applyFilters(true);
    }

    function activateButton(selector, value) {
      root.querySelectorAll(selector).forEach(function (button) {
        var active = value === (button.dataset.wqxLetter || button.dataset.wqxGoal);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    root.querySelectorAll('[data-wqx-letter]').forEach(function (button) {
      button.addEventListener('click', function () {
        state.letter = button.dataset.wqxLetter;
        activateButton('[data-wqx-letter]', state.letter);
        filterAfterHydration();
      });
    });

    root.querySelectorAll('[data-wqx-goal]').forEach(function (button) {
      button.addEventListener('click', function () {
        state.goal = button.dataset.wqxGoal;
        activateButton('[data-wqx-goal]', state.goal);
        filterAfterHydration();
      });
    });

    if (search) {
      search.addEventListener('input', function () {
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(function () {
          state.q = search.value.trim().toLowerCase();
          filterAfterHydration();
        }, 160);
      });
    }

    var reset = root.querySelector('[data-wqx-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        state = { q: '', letter: 'all', goal: 'all' };
        if (search) search.value = '';
        activateButton('[data-wqx-letter]', 'all');
        activateButton('[data-wqx-goal]', 'all');
        applyFilters(true);
      });
    }

    if (loadButton) loadButton.addEventListener('click', loadNext);
    if (loadMode === 'infinite' && loadButton && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) loadNext();
      }, { rootMargin: '400px 0px' });
      observer.observe(loadButton);
    }

    var params = new URLSearchParams(window.location.search);
    state.q = (params.get('ingredient_q') || '').trim().toLowerCase();
    state.letter = (params.get('letter') || 'all').toLowerCase();
    state.goal = (params.get('goal') || 'all').toLowerCase();
    if (search) search.value = state.q;
    activateButton('[data-wqx-letter]', state.letter);
    activateButton('[data-wqx-goal]', state.goal);
    sortCards();
    if (state.q || state.letter !== 'all' || state.goal !== 'all') filterAfterHydration();
    else applyFilters(false);
  }

  function scan(scope) {
    (scope || document).querySelectorAll('[data-wqx-index]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
})();

/* ============================================================
   WQ controlled blog-tag labels
   Shopify's native article header prints the raw tag value.
   Keep canonical taxonomy keys in the URL/data layer while
   presenting a readable label to customers.
   ============================================================ */
(function () {
  var LABELS = {
    'type_standards': ['Standards & How-to', 'Normas y guías'],
    'type_comparison': ['Ingredient Comparison', 'Comparación de ingredientes'],
    'type_evidence': ['Evidence Review', 'Revisión de evidencia'],
    'type_dosage': ['Dosage & Timing', 'Dosis y horario'],
    'type_basics': ['Deficiency & Basics', 'Fundamentos y deficiencia'],
    'type_stack': ['Stacking & Routines', 'Combinaciones y rutinas'],
    'goal_sleep': ['Sleep & Stress', 'Sueño y estrés'],
    'goal_heart-health': ['Heart & Metabolic', 'Salud cardiovascular y metabólica'],
    'series_quality-intelligence': ['Quality Intelligence', 'Inteligencia de calidad'],
    'audience_b2b': ['For Business', 'Para empresas']
  };

  function titleCase(value) {
    return value
      .split('-')
      .filter(Boolean)
      .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
      .join(' ');
  }

  function labelTags(root) {
    var isSpanish = (document.documentElement.lang || '').toLowerCase().indexOf('es') === 0;
    (root || document).querySelectorAll('a[href*="/blogs/"][href*="/tagged/"]').forEach(function (link) {
      var raw = (link.textContent || '').trim();
      if (!/^(?:type|goal|ingredient|topic|series|audience)_/.test(raw)) return;
      var known = LABELS[raw];
      var fallback = raw.replace(/^(?:type|goal|ingredient|topic|series|audience)_/, '');
      link.textContent = known ? known[isSpanish ? 1 : 0] : titleCase(fallback);
      link.setAttribute('data-wq-tag-key', raw);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { labelTags(document); });
  } else {
    labelTags(document);
  }
  document.addEventListener('shopify:section:load', function (event) { labelTags(event.target); });
})();

/* -------------------------------------------------------------------------
   WQ header — "Ask Clarity" opens the chat instead of navigating.
   The menu item is data (scripts/nav/wq-navigation.json); its href ending in
   #ask is the contract between the menu, the pill styling in
   wq-custom-style.css, and this handler. With JS off the link still lands on
   the Clarity Engine page, so nothing is lost.
   ------------------------------------------------------------------------- */
(function () {
  var HOOK = 'a[href$="#ask"]';

  function launcherButton() {
    var root = document.querySelector('.wq-chat-launcher-root');
    return root ? root.querySelector('.wq-cl-btn') : null;
  }

  function closeDrawer() {
    var drawer = document.querySelector('header-drawer details[open]');
    if (drawer) drawer.removeAttribute('open');
    document.documentElement.style.overflow = '';
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest ? event.target.closest(HOOK) : null;
    if (!link) return;
    var btn = launcherButton();
    if (!btn) return; // launcher absent on this page — let the link navigate
    event.preventDefault();
    closeDrawer();
    var root = document.querySelector('.wq-chat-launcher-root');
    if (root && root.classList.contains('is-open')) return; // already open
    btn.click();
  });
})();

/* ============================================================
   WQ card enhancer — the catalogue card, applied to the native
   product cards on every grid in the theme.

   The A–Z catalogue page builds its own cards, so it can put the
   ingredient name on the card face and keep the long marketing title
   behind hover. Native cards cannot: their markup is fixed and a
   native card file may not be edited. So this reshapes the cards that
   are already on the page:

     · the heading becomes the ingredient name, with the pack size on
       its own line underneath
     · the full title moves into a hover panel, together with the type,
       format and goal facts published by sections/wq-card-enhance
     · the theme's own quick-add button is left where it is and simply
       restyled by CSS — so Add to cart still goes through the theme's
       product-form and opens the cart drawer

   Remove sections/wq-card-enhance from a template and the cards keep
   working; they just lose the facts in the hover panel.
   ============================================================ */
/* One reading of a catalogue title, for every surface that shows one.
   Titles are built as "<Name> <Form> — <dose> per serving, <pack> (<servings>)"
   by scripts/bulk-300/p2-import.mjs. Card grids want the name and the pack; a
   cart line wants the name, the dose and the pack; the Clarity Engine wants the
   name and the dose. All of them must agree on where the name ends, so the
   split lives here and each caller takes the fields it needs.
   Mirrors scripts/catalog/build-product-index.mjs and the copy in
   sections/wq-quiz-advanced.liquid. */
function wqParseTitle(title) {
  var SEP = /\s+[—–-]\s+/;
  var TRAILING_FORM = /\s+(Capsules?|Powder|Tablets?|Softgels?|Gummies|Liquid|Drops|Chewables?|Lozenges?)$/i;
  var DOSE = /(\d[\d.,]*\s*(?:mcg|mg|g|iu|billion\s+cfu|cfu|ml))\b/i;
  var t = String(title || '').trim();
  var parts = t.split(SEP);
  var head = parts[0] || t;
  var tail = parts.slice(1).join(' — ');
  var form = '';
  var fm = head.match(TRAILING_FORM);
  if (fm) { form = fm[1]; head = head.slice(0, head.length - fm[0].length); }
  var name = head.trim().replace(/[,\s]+$/, '') || t;

  var spec = '';
  if (tail) {
    var bare = tail.replace(/\([^)]*\)/g, '').trim().replace(/[,\s]+$/, '');
    var bits = bare.split(/,\s*/).filter(Boolean);
    var last = bits[bits.length - 1] || '';
    // Keep it only when it reads as a quantity: "180 capsules", "1 kg".
    if (/^[\d.]+\s*\S/.test(last) && !/per serving|daily/i.test(last)) spec = last;
  }
  var dm = tail.match(DOSE) || t.match(DOSE);
  // Unit casing is inconsistent in the catalogue ("10000 iu"): harmless inside
  // a long title, not harmless once the amount stands on its own.
  var dose = dm ? dm[1].replace(/\s+/g, ' ').trim()
    .replace(/\b(iu|cfu)\b/gi, function (u) { return u.toUpperCase(); })
    .replace(/\b(mcg|mg|ml)\b/gi, function (u) { return u.toLowerCase(); }) : '';

  return { name: name, spec: spec, dose: dose, form: form, full: t };
}

(function () {
  var ES = (document.documentElement.lang || '').toLowerCase().indexOf('es') === 0;
  var ADD_SHORT = ES ? 'Añadir' : 'Add';
  var ADD_LONG = ES ? 'Añadir al carrito' : 'Add to cart';
  var ADD_URL = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add';

  var data = null;
  var node = document.getElementById('wq-card-data');
  if (node) {
    try { data = (JSON.parse(node.textContent) || {}).items || null; } catch (e) { data = null; }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function pretty(s) { return String(s || '').replace(/-/g, ' '); }

  var parseTitle = wqParseTitle;

  function enhance(wrap) {
    if (wrap.dataset.wqEnhanced) return;
    var inner = wrap.querySelector('.card__inner');
    var link = wrap.querySelector('a[href*="/products/"]');
    if (!inner || !link) return;

    var headings = wrap.querySelectorAll('.card__heading');
    if (!headings.length) return;

    var full = '';
    for (var i = 0; i < headings.length && !full; i++) {
      var a = headings[i].querySelector('a');
      if (a) full = (a.textContent || '').trim();
    }
    if (!full) return;

    var parsed = parseTitle(full);
    wrap.dataset.wqEnhanced = '1';
    wrap.classList.add('wqce-on');

    // The visible name shortens; the accessible name keeps the full title, so
    // nothing is lost to a screen reader. aria-labelledby would win over
    // aria-label, so it goes.
    Array.prototype.forEach.call(headings, function (h) {
      var a = h.querySelector('a');
      if (!a) return;
      a.removeAttribute('aria-labelledby');
      a.setAttribute('aria-label', parsed.full);
      a.title = parsed.full;
      a.textContent = parsed.name;
    });

    // Pack size, directly under the name, in the card's own flow.
    var outer = wrap.querySelector('.card__content .card__heading') || headings[headings.length - 1];
    if (parsed.spec && outer && !wrap.querySelector('.wqce-spec')) {
      var sp = document.createElement('div');
      sp.className = 'wqce-spec';
      sp.textContent = parsed.spec;
      outer.insertAdjacentElement('afterend', sp);
    }

    var item = null;
    var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    if (m && data) item = data[m[1]] || null;

    // The theme's quick-add keeps its markup and its behaviour — it is the
    // theme's product-form, so it still opens the cart drawer. Only the label
    // shortens, because a full-width "Add to cart" cannot sit beside the price
    // at the size this card wants it. The button's accessible name still names
    // the product.
    var atc = wrap.querySelector('.quick-add__submit');
    if (atc) {
      // Search results carry cards with no quick-add at all; only mark the ones
      // that have a pill to place.
      wrap.classList.add('wqce-hasadd');

      // Into the price row, so price and pill sit on one line and take their
      // insets from the same box — the card's own padding, whatever a template
      // or a theme setting makes it. Positioning the pill against the card
      // instead left it hard against the edge while the price stayed indented.
      //
      // The button keeps its markup and stays inside <product-form>, so the
      // theme's own AJAX and the cart drawer are untouched by the move.
      var quickAdd = wrap.querySelector('.quick-add');
      var row = wrap.querySelector('.card-information');
      if (quickAdd && row && quickAdd.parentElement !== row) row.appendChild(quickAdd);

      var label = atc.querySelector('.main-atc__label__text');
      if (label && !atc.dataset.wqLabel) {
        atc.dataset.wqLabel = '1';
        atc.setAttribute('aria-label', ADD_LONG + ': ' + parsed.full);
        atc.removeAttribute('aria-labelledby');
        label.textContent = ADD_SHORT;
      }
    } else if (item && item.v && item.a) {
      // Search results have no quick-add setting in this theme, so there is
      // nothing to restyle. Build the same pill from the catalogue data and let
      // the drawer handler below carry it — the A-Z page's Add, exactly.
      var row2 = wrap.querySelector('.card-information');
      if (row2 && !row2.querySelector('.wqce-add')) {
        wrap.classList.add('wqce-hasadd');
        var f = document.createElement('form');
        f.className = 'wqce-add';
        f.method = 'post';
        f.action = ADD_URL;
        f.innerHTML =
          '<input type="hidden" name="id" value="' + esc(item.v) + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" aria-label="' + esc(ADD_LONG + ': ' + parsed.full) + '">' + esc(ADD_SHORT) + '</button>';
        row2.appendChild(f);
      }
    }

    // Hover panel over the blurred bottle: the full title always, plus the
    // facts when this template carries the data section.
    var html = '<span class="wqce-facts-in"><span class="wqce-facts-full">' + esc(parsed.full) + '</span>';
    if (item) {
      var bits = [];
      if (item.c) bits.push(pretty(item.c));
      if (item.f) bits.push(pretty(item.f));
      if (bits.length) {
        html += '<span class="wqce-facts-meta">' + bits.map(function (b) {
          return '<span>' + esc(b) + '</span>';
        }).join('') + '</span>';
      }
      var goals = String(item.g || '').split('|').filter(Boolean).slice(0, 3);
      if (goals.length) {
        html += '<span class="wqce-facts-tags">' + goals.map(function (g) {
          return '<span>' + esc(pretty(g)) + '</span>';
        }).join('') + '</span>';
      }
    }
    html += '</span>';

    var panel = document.createElement('div');
    panel.className = 'wqce-facts';
    panel.setAttribute('aria-hidden', 'true');
    panel.innerHTML = html;
    inner.appendChild(panel);
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.product-grid .card-wrapper').forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { run(document); });
  } else {
    run(document);
  }
  // Grids paginate, filter and load more in place on this theme.
  document.addEventListener('shopify:section:load', function (e) { run(e.target); });
  if (window.MutationObserver) {
    new MutationObserver(function () { run(document); })
      .observe(document.body, { childList: true, subtree: true });
  }
})();

/* ============================================================
   WQ catalogue add to cart — the A–Z page builds its own cards, so its
   Add button is a plain form post that still works without JS. When the
   cart drawer is present, post to /cart/add.js instead and hand the
   returned sections to the drawer, so the shopper never leaves the grid.
   Any failure falls back to the real form post.

   Native cards need none of this: their Add button is the theme's own
   quick-add, which already goes through product-form and the drawer.
   ============================================================ */
(function () {
  function drawer() { return document.querySelector('cart-drawer'); }

  function render(sections) {
    var d = drawer();
    if (!d || !sections || typeof d.renderContents !== 'function') return false;
    d.renderContents({ sections: sections });
    var el = document.querySelector('cart-drawer.drawer');
    if (el) el.classList.remove('is-empty');
    return true;
  }

  function busy(btn, on) {
    if (!btn) return;
    btn.classList.toggle('is-busy', !!on);
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
    btn.disabled = !!on;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.closest ? event.target.closest('form.wqcat-add, form.wqce-add, form.wqpd-form') : null;
    if (!form || !drawer() || !window.fetch) return; // no drawer: let it post

    var id = form.querySelector('input[name="id"]');
    if (!id || !id.value) return;

    event.preventDefault();
    var btn = form.querySelector('button[type="submit"]') || form.querySelector('button');
    busy(btn, true);

    // Read the form rather than assuming a single unit: the product page adds
    // a quantity stepper and, once the store has selling plans, a subscribe
    // choice. Both are ordinary form fields, so both travel.
    var qtyField = form.querySelector('[name="quantity"]');
    var planField = form.querySelector('[name="selling_plan"]:checked') || form.querySelector('[name="selling_plan"]');
    var item = { id: id.value, quantity: Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1) };
    if (planField && planField.value) item.selling_plan = planField.value;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [item],
        sections: ['cart-drawer', 'cart-icon-bubble']
      })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || 'add to cart failed');
          return body;
        });
      })
      .then(function (body) {
        busy(btn, false);
        // The drawer is the whole point; if it cannot render, post for real.
        if (!render(body.sections)) form.submit();
      })
      .catch(function () {
        busy(btn, false);
        form.submit();
      });
  });
})();

/* ============================================================
   WQ product page — gallery, quantity, variant and the stack
   builder. The Add button itself needs nothing here: its form
   carries id, quantity and selling_plan, and the drawer handler
   above already reads all three.
   ============================================================ */
(function () {
  var root = document.querySelector('[data-wqpd]');
  if (!root) return;

  // --- gallery: thumbnails swap the main image, nothing else ---------------
  var main = root.querySelector('#wqpd-main-image');
  root.addEventListener('click', function (e) {
    var thumb = e.target.closest ? e.target.closest('[data-wqpd-thumb]') : null;
    if (!thumb || !main) return;
    var full = thumb.getAttribute('data-full');
    if (!full) return;
    main.removeAttribute('srcset');
    main.src = full;
    root.querySelectorAll('[data-wqpd-thumb]').forEach(function (t) {
      t.classList.toggle('is-active', t === thumb);
    });
  });

  // --- quantity stepper ----------------------------------------------------
  root.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-wqpd-qty]') : null;
    if (!btn) return;
    var form = btn.closest('form');
    var input = form && form.querySelector('[name="quantity"]');
    if (!input) return;
    var next = (parseInt(input.value, 10) || 1) + parseInt(btn.getAttribute('data-wqpd-qty'), 10);
    input.value = Math.max(1, next);
  });

  // --- variant: keep the posted id, the price and the button in step -------
  var select = root.querySelector('[data-wqpd-variant-select]');
  if (select) {
    select.addEventListener('change', function () {
      var opt = select.options[select.selectedIndex];
      var idField = root.querySelector('[data-wqpd-id]');
      var price = root.querySelector('[data-wqpd-price]');
      var atc = root.querySelector('[data-wqpd-atc]');
      var label = root.querySelector('[data-wqpd-atc-label]');
      if (idField) idField.value = select.value;
      if (price && opt.dataset.price) price.textContent = opt.dataset.price;
      if (atc) {
        var available = opt.dataset.available === '1';
        atc.disabled = !available;
        if (label) label.textContent = available ? (atc.dataset.labelIn || label.textContent) : (atc.dataset.labelOut || label.textContent);
      }
    });
  }


  // --- stack preview drawer -----------------------------------------------
  // View used to navigate away from the page the shopper is buying from.
  // It now fills a drawer from the card already rendered beside the button,
  // so there is no request and no page change; the full product page is one
  // click further in for anyone who wants it.
  var peek = root.querySelector('[data-wqpd-peek-drawer]');
  if (peek) {
    var peekBody = peek.querySelector('[data-wqpd-peek-body]');
    var peekOpener = null;

    function closePeek() {
      if (peek.hidden) return;
      peek.classList.remove('is-open');
      var done = function () {
        peek.hidden = true;
        peekBody.innerHTML = '';
        if (peekOpener) { peekOpener.focus(); peekOpener = null; }
      };
      var panel = peek.querySelector('.wqpd-peek-panel');
      if (panel && getComputedStyle(panel).transitionDuration !== '0s') {
        panel.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 400); // in case the transition never fires
      } else {
        done();
      }
    }

    function openPeek(handle, opener) {
      var src = root.querySelector('[data-wqpd-peek-src="' + handle + '"]');
      if (!src) return;
      peekBody.innerHTML = '';
      peekBody.appendChild(src.content.cloneNode(true));
      peekOpener = opener || null;
      peek.hidden = false;
      // Force a reflow so the closed state is committed, then open. Doing this
      // with requestAnimationFrame instead would leave the panel stuck
      // off-screen wherever frames are not being produced -- a background tab,
      // a headless viewport -- because the callback never runs.
      void peek.offsetHeight;
      peek.classList.add('is-open');
      var focusable = peekBody.querySelector('button, a[href]') || peek.querySelector('[data-wqpd-peek-close]');
      if (focusable) focusable.focus();
    }

    root.addEventListener('click', function (e) {
      var opener = e.target.closest ? e.target.closest('[data-wqpd-peek]') : null;
      if (opener) {
        e.preventDefault();
        openPeek(opener.getAttribute('data-wqpd-peek'), opener);
        return;
      }
      if (e.target.closest && e.target.closest('[data-wqpd-peek-close]')) closePeek();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !peek.hidden) closePeek();
    });

    // Adding from the drawer opens the cart drawer; two panels over each
    // other is a mess, so this one steps out of the way.
    peek.addEventListener('submit', function () { setTimeout(closePeek, 60); });
  }

  // --- stack builder: one request, however many boxes are ticked -----------
  var bundle = root.querySelector('[data-wqpd-bundle]');
  if (!bundle) return;
  var addBtn = bundle.querySelector('[data-wqpd-bundle-add]');
  var boxes = [].slice.call(bundle.querySelectorAll('[data-wqpd-bundle-id]'));

  function picked() {
    return boxes.filter(function (b) { return b.checked; }).map(function (b) {
      return { id: b.getAttribute('data-wqpd-bundle-id'), quantity: 1 };
    });
  }
  function sync() { if (addBtn) addBtn.disabled = picked().length === 0; }
  boxes.forEach(function (b) { b.addEventListener('change', sync); });
  sync();

  if (!addBtn) return;
  addBtn.addEventListener('click', function () {
    var items = picked();
    if (!items.length) return;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !window.fetch) { return; } // no drawer: leave the links to do the work

    addBtn.disabled = true;
    addBtn.classList.add('is-busy');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'] })
    })
      .then(function (res) { return res.json().then(function (b) { if (!res.ok) throw new Error(b.description || 'add failed'); return b; }); })
      .then(function (body) {
        addBtn.classList.remove('is-busy');
        if (typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: body.sections });
          var el = document.querySelector('cart-drawer.drawer');
          if (el) el.classList.remove('is-empty');
        }
        boxes.forEach(function (b) { b.checked = false; });
        sync();
      })
      .catch(function () {
        addBtn.classList.remove('is-busy');
        sync();
      });
  });
})();

/* ============================================================
   WQ recently viewed — what this visitor has been reading

   The Clarity Engine's express lane used to offer the same nine
   suggestions to everyone, including people who had just spent ten
   minutes on a magnesium page. This records what was looked at so the
   engine can offer it back as a starting point.

   Deliberately small: handles only, on this device only, never sent
   anywhere by this file. No titles, no timestamps beyond ordering, no
   identifiers. Clearing site data clears it, and nothing else depends
   on it — the express lane falls back to the merchant's own chips.

   Article goal tags come from the article page itself (see
   sections/wq-article-relations.liquid); the mapping from the blog's
   goal vocabulary to the engine's lives in the quiz section, next to
   the taxonomy it belongs to.
   ============================================================ */
(function () {
  var KEY = 'wq-seen';
  var CAP = 12;

  var product = location.pathname.match(/\/products\/([^/?#]+)/);
  var article = location.pathname.match(/\/blogs\/[^/]+\/([^/?#]+)/);
  if (!product && !article) return;

  var entry = null;
  if (product) {
    entry = { t: 'p', h: product[1] };
  } else {
    var node = document.querySelector('[data-wq-article-goal]');
    entry = { t: 'a', h: article[1] };
    var goal = node && node.getAttribute('data-wq-article-goal');
    if (goal) entry.g = goal;
  }

  try {
    var list = JSON.parse(localStorage.getItem(KEY) || '[]');
    if (!Array.isArray(list)) list = [];
    list = list.filter(function (v) { return v && v.h !== entry.h; });
    list.unshift(entry);
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP)));
  } catch (e) { /* private mode, quota, disabled storage — all fine */ }
})();

/* ============================================================
   WQ cart drawer — the line item, read the way the rest of the
   site reads a product.

   The drawer showed the full marketing title, so a single line
   ran to three: "Potassium Tablets — 595 mg per serving, 300
   tablets (300 servings)". Every grid on this site already
   shortens that to the ingredient name; a cart is the one place
   a customer is checking WHAT they are buying, so it gets the
   name plus the two facts that decide it — the per-serving
   amount and the pack size.

   Native markup is untouched: this reshapes text nodes only,
   and the drawer re-renders from the sections API on every
   quantity change, so an observer re-applies it. Remove this
   block and the drawer returns to Shrine's own rendering.
   ============================================================ */
(function () {
  var SPEC_CLASS = 'wq-cart-spec';

  function enhance(scope) {
    var names = (scope || document).querySelectorAll('.cart-item__name');
    Array.prototype.forEach.call(names, function (el) {
      if (el.dataset.wqCart === '1') return;
      var full = (el.textContent || '').trim();
      if (!full) return;
      var p = wqParseTitle(full);
      // Nothing to shorten (a title that is already just a name) still gets
      // marked, so the observer does not reconsider it on every mutation.
      el.dataset.wqCart = '1';
      if (p.name && p.name !== full) {
        el.title = full;
        el.setAttribute('aria-label', full);
        el.textContent = p.name;
      }
      var spec = [p.dose, p.spec].filter(Boolean).join(' · ');
      if (!spec) return;
      var holder = el.parentNode;
      if (!holder || holder.querySelector('.' + SPEC_CLASS)) return;
      var s = document.createElement('span');
      s.className = SPEC_CLASS;
      s.textContent = spec;
      el.insertAdjacentElement('afterend', s);
    });
  }

  function start() {
    // querySelectorAll, not querySelector: the drawer contains its own
    // cart-items, so asking for "the" one returned the drawer's copy and the
    // cart page's table was left with full titles while the drawer beside it
    // showed short ones.
    var roots = document.querySelectorAll('cart-drawer, cart-items, .cart-items, .cart__items');
    Array.prototype.forEach.call(roots, function (root) {
      enhance(root);
      // The theme replaces the item list wholesale after every quantity
      // change; without this the shortened titles would last until the first
      // "+" click. Overlapping roots are harmless — enhance() is idempotent.
      new MutationObserver(function () { enhance(root); })
        .observe(root, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
